/**
 * Core implementation for codebase search.
 * Calls the Morph rerank service for two-stage semantic search over the
 * code-storage host, through the shared `MorphAPIClient` transport.
 */
import { MorphAPIClient } from '../../core/client.js';
import { APIResource } from '../../core/resource.js';
import { MorphError } from '../utils/resilience.js';
import { logger } from '../../logger.js';
import type { CodebaseSearchConfig, CodebaseSearchInput, CodebaseSearchResult } from './types.js';

const DEFAULT_TIMEOUT = 30000;

const emptyStats = { totalResults: 0, candidatesRetrieved: 0, searchTimeMs: 0 };

/**
 * Run a search against `/v1/codebase_search` on the repos host. HTTP failures
 * are returned in-band as `{ success: false, error }` (never thrown), matching
 * the long-standing contract callers rely on.
 */
async function runSearch(
  client: MorphAPIClient,
  input: CodebaseSearchInput,
  repoId: string,
  timeout: number,
  baseURL?: string,
): Promise<CodebaseSearchResult> {
  const startTime = Date.now();
  logger.debug('CodebaseSearch', 'request', { query: input.query.slice(0, 100), repo_id: repoId });

  try {
    const data = await client.post<{ results?: unknown[]; stats?: CodebaseSearchResult['stats'] }>(
      '/v1/codebase_search',
      {
        baseURL: baseURL ?? client.reposURL,
        timeout,
        body: {
          query: input.query,
          repoId,
          targetDirectories: input.target_directories || [],
          limit: input.limit || 10,
          candidateLimit: 50,
        },
      },
    );

    const elapsed = Date.now() - startTime;
    logger.debug('CodebaseSearch', 'response', { results_count: data.results?.length || 0, latency_ms: elapsed });
    return {
      success: true,
      results: (data.results as CodebaseSearchResult['results']) || [],
      stats: data.stats || { totalResults: 0, candidatesRetrieved: 0, searchTimeMs: elapsed },
    };
  } catch (error) {
    const message =
      error instanceof MorphError && error.statusCode
        ? `Search failed (${error.statusCode}): ${error.message}`
        : error instanceof Error
          ? error.message
          : 'Unknown error';
    logger.error('CodebaseSearch', 'error', { error: message, latency_ms: Date.now() - startTime });
    return { success: false, results: [], stats: { ...emptyStats }, error: message };
  }
}

/**
 * CodebaseSearch client for programmatic semantic search
 *
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).codebaseSearch`).
 * Standalone clients remain only for backwards compatibility and may be removed in a future
 * major version — do not use them in new code.
 */
export class CodebaseSearchClient extends APIResource {
  private readonly timeout: number;

  constructor(
    clientOrConfig: MorphAPIClient | { apiKey?: string; debug?: boolean; timeout?: number; retryConfig?: any } = {},
  ) {
    super(
      clientOrConfig instanceof MorphAPIClient
        ? clientOrConfig
        : new MorphAPIClient({
            apiKey: clientOrConfig.apiKey,
            timeout: clientOrConfig.timeout ?? DEFAULT_TIMEOUT,
            retryConfig: clientOrConfig.retryConfig,
            debug: clientOrConfig.debug,
          }),
    );
    this.timeout = (clientOrConfig instanceof MorphAPIClient ? undefined : clientOrConfig.timeout) ?? DEFAULT_TIMEOUT;
  }

  /**
   * Execute a semantic code search
   *
   * @param input - Search parameters including query, repoId, and target directories
   * @param overrides - Optional config overrides for this operation
   * @returns Search results with ranked code matches
   */
  async search(
    input: { query: string; repoId: string; target_directories?: string[]; explanation?: string; limit?: number },
    overrides?: { searchUrl?: string; timeout?: number },
  ): Promise<CodebaseSearchResult> {
    return runSearch(
      this._client,
      { query: input.query, target_directories: input.target_directories, explanation: input.explanation, limit: input.limit },
      input.repoId,
      overrides?.timeout ?? this.timeout,
      overrides?.searchUrl,
    );
  }
}

/**
 * Execute semantic code search (standalone — builds its own transport).
 * Throws on a missing API key; returns `{ success: false, error }` on HTTP failure.
 */
export async function executeCodebaseSearch(
  input: CodebaseSearchInput,
  config: CodebaseSearchConfig,
): Promise<CodebaseSearchResult> {
  const apiKey = config.apiKey || process.env.MORPH_API_KEY;
  if (!apiKey) {
    throw new Error('MORPH_API_KEY not found. Set environment variable or pass in config');
  }

  const client = new MorphAPIClient({
    apiKey,
    reposURL: config.searchUrl,
    timeout: config.timeout ?? DEFAULT_TIMEOUT,
    retryConfig: config.retryConfig,
    debug: config.debug,
  });

  return runSearch(client, input, config.repoId, config.timeout ?? DEFAULT_TIMEOUT, config.searchUrl);
}
